// 추가 옵션이 있는 상품의 담기 모달.
// 기본 단위 수량 + 옵션별 개수를 고르고, 최종 금액과 총 중량(기본+추가)을 실시간으로 보여준 뒤 장바구니에 담는다.
//  - 옵션이 없는 상품은 이 모달을 쓰지 않는다(기존 카드 수량 스테퍼로 즉시 담기).
//  - 옵션 선택은 필수가 아니다(0개면 기본 수량만 담긴다).
//  - 중량이 있는 옵션은 준비완료 때 기본 중량과 합산해 실중량으로 정산된다(합산 정산).

import { useMemo, useState } from "react";
import { Minus, Plus, X } from "lucide-react";
import {
  CART_ITEM_QUANTITY_MAX,
  formatWeightG,
  lineExpectedGramsG,
  lineExpectedPriceKrw,
  resolveUnitLabel,
  type CartItemOptionInput,
  type ProductDto,
} from "@oo/shared";
import { useUnits } from "./useUnits.ts";

interface Props {
  product: ProductDto;
  onClose: () => void;
  // 담기 확정. 상위(카드/상세)가 실제 cart.addItem 호출과 토스트를 담당한다.
  onConfirm: (quantity: number, options: CartItemOptionInput[]) => Promise<void> | void;
  busy?: boolean;
}

// 추가 옵션 선택 모달(수량 + 옵션 개수 + 합계/총중량).
export default function ProductOptionsModal({ product, onClose, onConfirm, busy }: Props) {
  const units = useUnits();
  const unitLabel = resolveUnitLabel(units, product.unit, product.packWeightG);
  const [quantity, setQuantity] = useState(1);
  // 옵션 id -> 개수. 0 이면 담지 않는다.
  const [optionQty, setOptionQty] = useState<Record<string, number>>({});

  // 기준 중량(g): 팩 상품은 1팩 중량, 그 외에는 단위 기준중량. 총 중량 표시에 쓴다.
  const baseGramsG = useMemo(() => {
    if (product.packWeightG) {
      return product.packWeightG;
    }
    return units.find((u) => u.code === product.unit)?.baseGrams ?? null;
  }, [product.packWeightG, product.unit, units]);

  // 선택된 옵션만 추려 금액/중량 계산에 넘긴다.
  const selected = useMemo(
    () =>
      product.options
        .filter((o) => (optionQty[o.id] ?? 0) > 0)
        .map((o) => ({
          optionId: o.id,
          label: o.label,
          addGrams: o.addGrams,
          addPriceKrw: o.addPriceKrw,
          quantity: optionQty[o.id] ?? 0,
        })),
    [product.options, optionQty],
  );

  const totalKrw = lineExpectedPriceKrw(product.priceKrw, quantity, selected);
  const totalG = lineExpectedGramsG(baseGramsG, quantity, selected);

  function bumpOption(optionId: string, delta: number) {
    setOptionQty((m) => {
      const next = Math.min(CART_ITEM_QUANTITY_MAX, Math.max(0, (m[optionId] ?? 0) + delta));
      return { ...m, [optionId]: next };
    });
  }

  async function submit() {
    if (busy) {
      return;
    }
    await onConfirm(
      quantity,
      selected.map((s) => ({ optionId: s.optionId, quantity: s.quantity })),
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink-900/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-options-title"
        className="flex max-h-[85vh] w-full max-w-screen-sm flex-col overflow-y-auto rounded-t-2xl bg-surface p-5 pb-[calc(1.25rem+var(--safe-bottom))] shadow-elevated sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 id="product-options-title" className="truncate text-lg font-bold text-ink-900">
              {product.name}
            </h2>
            <p className="mt-0.5 text-sm text-ink-600">
              {product.priceKrw.toLocaleString()}원{unitLabel ? ` / ${unitLabel}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="shrink-0 rounded-md p-1 text-ink-600 hover:bg-ink-100"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* 기본 단위 수량 */}
        <div className="mt-4 flex items-center justify-between gap-2 rounded-lg border border-ink-200 p-3">
          <span className="text-sm font-medium text-ink-800">
            {unitLabel || "수량"}
          </span>
          <Stepper
            value={quantity}
            min={1}
            onChange={(next) => setQuantity(next)}
            ariaLabel="기본 수량"
          />
        </div>

        {/* 추가 옵션 */}
        <div className="mt-3 flex flex-col gap-2">
          <p className="text-sm font-semibold text-ink-700">
            추가 옵션 <span className="text-xs font-normal text-ink-500">(선택)</span>
          </p>
          <ul className="flex flex-col gap-2">
            {product.options.map((o) => (
              <li
                key={o.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-ink-200 p-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink-800">{o.label}</p>
                  <p className="text-xs text-ink-600">
                    +{o.addPriceKrw.toLocaleString()}원
                    {o.addGrams > 0 ? ` · +${formatWeightG(o.addGrams)}` : ""}
                  </p>
                </div>
                <Stepper
                  value={optionQty[o.id] ?? 0}
                  min={0}
                  onChange={(next) => bumpOption(o.id, next - (optionQty[o.id] ?? 0))}
                  ariaLabel={`${o.label} 개수`}
                />
              </li>
            ))}
          </ul>
        </div>

        {/* 합계 요약: 최종 담기 금액 + 총 중량(기본+추가) */}
        <div className="mt-4 flex flex-col gap-1 rounded-lg bg-ink-50 p-3">
          {totalG > 0 ? (
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-ink-700">총 중량</span>
              <span className="font-medium tabular-nums text-ink-900">{formatWeightG(totalG)}</span>
            </div>
          ) : null}
          <div className="flex items-baseline justify-between">
            <span className="text-base font-semibold text-ink-800">합계</span>
            <span className="text-2xl font-bold tabular-nums text-ink-900">
              {totalKrw.toLocaleString()}원
            </span>
          </div>
          {product.weightAdjustable ? (
            <p className="text-[11px] text-ink-500">
              정육 상품은 실제 손질 중량에 따라 최종 금액이 조금 달라질 수 있어요.
            </p>
          ) : null}
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="mt-4 flex min-h-touch items-center justify-center rounded-md bg-brand-600 px-4 text-base font-semibold text-white hover:bg-brand-700 disabled:bg-ink-300"
        >
          {busy ? "담는 중..." : "장바구니 담기"}
        </button>
      </div>
    </div>
  );
}

// 수량 스테퍼(기본 수량/옵션 개수 공용). min 미만으로는 내려가지 않는다.
function Stepper({
  value,
  min,
  onChange,
  ariaLabel,
}: {
  value: number;
  min: number;
  onChange: (next: number) => void;
  ariaLabel: string;
}) {
  return (
    <div
      className="flex shrink-0 items-center rounded-full border border-brand-600 text-brand-700"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label="줄이기"
        className="flex h-9 w-9 items-center justify-center disabled:opacity-40"
      >
        <Minus size={16} aria-hidden="true" />
      </button>
      <span className="min-w-7 text-center text-sm font-semibold tabular-nums">{value}</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(CART_ITEM_QUANTITY_MAX, value + 1))}
        disabled={value >= CART_ITEM_QUANTITY_MAX}
        aria-label="늘리기"
        className="flex h-9 w-9 items-center justify-center disabled:opacity-40"
      >
        <Plus size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
